/**
 * The rating tier lookups must be fed: tiers are ALWAYS graded at 0 chem, and
 * a compared player's `metaRating` reflects their slot's chem/chemstyle after
 * the fetch. Undefined (caller falls back) while the 0-chem entry for the
 * graded role isn't in the list yet.
 */
export const zeroChemMetaRating = (player, gradedRoleId) => player.metaRatings?.find((m) => m.chemistry === 0 &&
    (m.playerRoleId == null ||
        m.playerRoleId === (gradedRoleId ?? player.playerRoleId)))?.metaRating;
/**
 * A section winner only means something when every column is graded under the
 * same role: with "Best Role", a Playmaker CAM's PlayStyles score and a
 * Defender CB's are answers to different questions, and the higher number
 * doesn't name a better player. So — an explicit role, or every player's own
 * best role happening to agree.
 */
export const scoresComparable = (players, hasExplicitRole) => hasExplicitRole || new Set(players.map((p) => p.playerRoleId)).size === 1;
/**
 * Highest meta rating, same comparability rule as the sections (a Playmaker
 * CAM's 88.6 and a Defender CB's 87.8 aren't a ranking). Winner only when
 * the ratings differ — an all-tie says nothing, so nobody gets the star.
 */
export const computeMetaRatingWinners = (players, comparable) => {
    const bestMetaRating = Math.max(...players.map((p) => p.metaRating ?? 0), 0);
    const metaRatingsDiffer = players.some((p) => p.metaRating != null && p.metaRating < bestMetaRating);
    return players.map((p) => comparable &&
        players.length > 1 &&
        bestMetaRating > 0 &&
        metaRatingsDiffer &&
        p.metaRating === bestMetaRating);
};
/**
 * Winning column indexes for a category's per-column scores (index-aligned
 * with the compared players; `undefined` = disabled/unscored column), ranked
 * by score — the tier is too coarse to separate players. Winners only when
 * the scores discriminate: an all-tie (or a lone scored column) says nothing,
 * so nobody wins. Empty when the columns aren't comparable or when no column
 * is scored at all. The winning number needn't be positive ("Correct Foot"
 * scores 0 on the right foot, negative on the wrong one — 0 wins outright).
 */
export const computeCategoryWinners = (scores, comparable) => {
    const scored = scores.filter((s) => s !== undefined);
    if (!comparable || scores.length < 2 || scored.length === 0) {
        return new Set();
    }
    const best = Math.max(...scored);
    // A column doesn't win for lack of scored opponents.
    if (!scored.some((s) => s < best))
        return new Set();
    return new Set(scores.flatMap((s, idx) => (s === best ? [idx] : [])));
};
/**
 * Column indexes holding the highest known value — but only when the row
 * discriminates: if every known value ties at the max (or nothing is known),
 * nobody wins. Two equal columns get no green; 5★/5★/4★ greens the two 5★s.
 * Unknown values aren't losers, so a value doesn't win just because the
 * other column hasn't loaded. (Raw-value rows — Weak Foot, Skill Moves,
 * attributes — which need no role-comparability gate, unlike
 * `computeCategoryWinners`.)
 */
export const strictWinners = (values) => {
    const known = values.filter((v) => v != null);
    if (known.length === 0)
        return new Set();
    const max = Math.max(...known);
    if (!known.some((v) => v < max))
        return new Set();
    return new Set(values.flatMap((v, idx) => (v === max ? [idx] : [])));
};
/**
 * Toolbar nudge towards a shared role: under "Best Role" with mixed own-best
 * roles the columns aren't comparable (nothing gets a winner), so suggest a
 * single role to grade everyone under — the role the FIRST player rates best
 * in (zero-chem, the same basis the comparison grades under) among the roles
 * every compared player can play. `metaRatings` arrive with the comparison
 * fetch; until then a just-staged set falls back to the first player's own
 * best role when it's shared. No shared positions → `sharedRoles: false`,
 * the no-suggestion variant.
 */
export const computeRoleMismatch = (players, playerRoles) => {
    const first = players[0];
    if (!first)
        return undefined;
    const sharedRoles = playerRoles.filter((role) => players.every((p) => p.positions?.includes(role.position)));
    let suggestedRole;
    let bestRating = 0;
    for (const role of sharedRoles) {
        const rating = first.metaRatings?.find((mr) => mr.playerRoleId === role.id && mr.chemistry === 0)?.metaRating;
        if (rating !== undefined && rating > bestRating) {
            bestRating = rating;
            suggestedRole = role;
        }
    }
    if (!suggestedRole) {
        suggestedRole = sharedRoles.find((r) => r.id === first.playerRoleId);
    }
    return { sharedRoles: sharedRoles.length > 0, suggestedRole };
};
//# sourceMappingURL=compare.js.map